import React, { useState } from "react";
import WorldMap from "./WorldMap";
import { Crosshair, Trash } from "@phosphor-icons/react";

export default function LocationPicker({ value, onChange }) {
  const [picking, setPicking] = useState(false);
  const [gpsLoading, setGpsLoading] = useState(false);
  const [gpsErr, setGpsErr] = useState("");

  const lat = value?.lat;
  const lng = value?.lng;
  const hasPosition = lat != null && lng != null;

  const useGps = () => {
    setGpsErr("");
    if (!navigator.geolocation) { setGpsErr("Il browser non supporta la geolocalizzazione."); return; }
    setGpsLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        onChange({
          ...(value || {}),
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
          alt: pos.coords.altitude ?? value?.alt,
        });
        setGpsLoading(false);
      },
      (err) => {
        setGpsErr(err.message || "Errore nella geolocalizzazione.");
        setGpsLoading(false);
      },
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };

  const onMapPick = (latlng) => {
    onChange({ ...(value || {}), lat: latlng.lat, lng: latlng.lng });
  };

  const clear = () => onChange({ lat: null, lng: null, alt: null, accuracy: null, label: value?.label || "" });

  return (
    <div className="space-y-3" data-testid="location-picker">
      <div className="flex flex-wrap gap-2 items-center">
        <button
          type="button"
          onClick={useGps}
          disabled={gpsLoading}
          className="btn-ghost text-sm inline-flex items-center gap-2"
          data-testid="gps-btn"
        >
          <Crosshair weight="duotone" size={16} /> {gpsLoading ? "GPS..." : "Usa posizione attuale"}
        </button>
        <button
          type="button"
          onClick={() => setPicking((v) => !v)}
          className={`text-sm inline-flex items-center gap-2 ${picking ? "btn-primary" : "btn-ghost"}`}
          data-testid="pick-toggle-btn"
        >
          {picking ? "Tocca sulla mappa..." : "Posiziona sulla mappa"}
        </button>
        {hasPosition && (
          <button
            type="button"
            onClick={clear}
            className="text-red-700 hover:bg-red-50 text-sm inline-flex items-center gap-1 px-2 py-1"
            data-testid="clear-location-btn"
          >
            <Trash size={14} /> Rimuovi
          </button>
        )}
      </div>

      {gpsErr && <div className="text-sm text-red-700">{gpsErr}</div>}

      <input
        type="text"
        className="input-base"
        placeholder="Etichetta del luogo (es: 'Bosco di Acquarossa, sentiero alto')"
        value={value?.label || ""}
        onChange={(e) => onChange({ ...(value || {}), label: e.target.value })}
        data-testid="location-label"
      />

      <div className="card overflow-hidden">
        <WorldMap
          center={hasPosition ? [lat, lng] : undefined}
          zoom={hasPosition ? 14 : 4}
          pickable={picking}
          onPick={(ll) => { onMapPick(ll); setPicking(false); }}
          pickedPosition={hasPosition ? [lat, lng] : null}
          flyTo={hasPosition ? [lat, lng] : null}
          height="min(calc(100dvh - 280px), 520px)"
        />
      </div>

      {hasPosition && (
        <div className="text-xs text-[#5C635A] font-mono">
          {lat.toFixed(5)}, {lng.toFixed(5)}
          {value?.alt != null && ` · ${Math.round(value.alt)} m s.l.m.`}
          {value?.accuracy != null && ` · precisione ±${Math.round(value.accuracy)}m`}
        </div>
      )}
    </div>
  );
}
